/**
 * EventTracker.
 * Provides possibility to traks events.
 *
 * @copyright Realeyes OU. All rights reserved.
 */

import StreamWriter from '../KinesisStreamWriter';
import Logger from '../Logger';
import sequence from '../../utils/Sequence';

const messageFormatVer = '1.0';

const noStreamText = (name) => `LogMessageTracker for '${name}' can not send messages no logStreamName is config)`;

export default class LogMessageTracker {
    /**
     * Creates EventTracker instance.
     * @param {string} name of registered tracker, will be used as logical stream name (later as s3 folder name)
     * @param {IdentityManager} identityService entity to get identity data on session level
     * @param {ConfigurationManager} configurationManager entity to get configuration
     * @param {TimeSyncService} timeSyncService time sync service entity
     */
    constructor(name, identityService, configurationManager, timeSyncService) {
        this.name = name;
        this.timeService = timeSyncService;
        this.streamWriter = null;
        this.identityService = identityService;
        this.logger = new Logger('LTK', configurationManager.isDebugMode());
        this.writePromises = [];
        this.configurationManager = configurationManager;
    }

    /**
     * returns stream writer for stream name defined in config
     * @returns {object} stream writer
     */
    getStreamWriter() {
        if (!this.streamWriter) {
            const streamName = this.configurationManager.config.aws.kinesis.logStreamName;
            this.streamWriter = streamName ? new StreamWriter(streamName, this.configurationManager) : null;
        }
        return this.streamWriter;
    }

    /**
     * Sends the event to server.
     *
     * @param {string} sender is who sends the activity
     * @param {Object} data is any data
     * @return {Promise.<boolean, Error>}
     */
    log(sender, data) {
        this.logger.logDebug(`received log message to send: sender-${sender}`);

        if (!sender) {
            const error = 'sender is empty!';
            this.logger.logError(error);
            return Promise.reject(new Error(error));
        }

        const streamWriter = this.getStreamWriter();
        if (!streamWriter) {
            const error = noStreamText(this.name);
            this.logger.logError(error);
            return Promise.reject(new Error(error));
        }

        const timeInfo = this.timeService.getCurrentTimeInfo();
        const sessionInfo = this.identityService.getSessionLevelIdentity();

        const sequenceNo = sequence.next('LogMessageTracker') + 1;
        const globalSequenceNo = sequence.next() + 1;
        const logMessageToSend = {
            prid: sessionInfo.projectId,
            sid: sessionInfo.sessionId,
            timestamp: timeInfo.clientTimestamp,
            serverTimestamp: timeInfo.serverTimestamp,
            type: this.name,
            data,
            sender: (typeof sender) === 'string' ? sender : JSON.stringify(sender),
            sequenceNo,
            globalSequenceNo,
            version: messageFormatVer,
        };

        this.logger.logInfo(JSON.stringify(logMessageToSend));

        const p = streamWriter.write(logMessageToSend);

        this.writePromises.push(p);
        const filterPromises = (promise) => this.writePromises.filter((pr) => pr === promise);

        p.then(() => {
            this.writePromises = filterPromises(p);
        }, () => {
            this.writePromises = filterPromises(p);
        });

        this.logger.logDebug(`the log message sent to stream writer: sender-${sender}`);
        return p;
    }

    /**
     * Waits untill all messages will be sent to server
     * @return {Promise.<boolean, Error>}
     */
    wait() {
        return Promise.all(this.writePromises);
    }
}
